import { Alert, Box } from '@mui/material';
import { CheckCircle, Visibility } from '@mui/icons-material';

import {
  CustomButton,
  CustomTextArea,
  CustomTextField,
  DecimalTextField,
  DrawerForm,
  DrawerFormActions,
  DrawerFormActionSummary,
  DrawerFormPreview,
  DrawerFormSection,
} from '../../../common/components';
import { drawerFieldSpan as span } from '../../../common/styles';
import { materialCell, materialMoney } from '../../../utils/materialDisplay';
import { VALUATION_TEXTS } from '../constants/materialValuationConstants';

const DRAWER_WIDTH = { xs: '100vw', md: 'min(720px, calc(100vw - 40px))' };

/**
 * Drawer de apertura de valoración: valoriza solo la cantidad sin valorizar de la existencia y
 * exige previsualizar antes de aprobarla.
 * @param {Object} props
 * @param {boolean} props.open Visibilidad del drawer.
 * @param {Object|null} props.form Existencia y datos de la apertura.
 * @param {Object|null} props.preview Resultado de la previsualización.
 * @param {string} props.error Mensaje de error del backend.
 * @param {boolean} props.isSubmitting Previsualización o aprobación en curso.
 * @param {boolean} props.canSubmit Formulario completo.
 * @param {Function} props.onClose Cierra el drawer.
 * @param {Function} props.onChange Cambia un campo del formulario.
 * @param {Function} props.onSubmit Previsualiza o aprueba.
 * @returns {JSX.Element}
 */
const MaterialOpeningDrawer = ({
  open,
  form,
  preview,
  error,
  isSubmitting,
  canSubmit,
  onClose,
  onChange,
  onSubmit,
}) => (
  <DrawerForm
    open={open}
    onClose={onClose}
    title={VALUATION_TEXTS.drawerTitle}
    width={DRAWER_WIDTH}
    footer={(
      <DrawerFormActions
        summary={(
          <DrawerFormActionSummary
            label={VALUATION_TEXTS.totalSummary}
            value={preview ? materialMoney(preview.total_value_ars) : '-'}
          />
        )}
      >
        <CustomButton variant="outlined" onClick={onClose} disabled={isSubmitting}>
          {VALUATION_TEXTS.cancel}
        </CustomButton>
        <CustomButton
          variant="base"
          startIcon={preview ? <CheckCircle /> : <Visibility />}
          onClick={onSubmit}
          isLoading={isSubmitting}
          disabled={!canSubmit}
        >
          {preview ? VALUATION_TEXTS.confirm : VALUATION_TEXTS.preview}
        </CustomButton>
      </DrawerFormActions>
    )}
  >
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25, minWidth: 0 }}>
      {error && (
        <Box sx={{ mx: 2 }}>
          <Alert severity="error">{error}</Alert>
        </Box>
      )}

      <DrawerFormSection title={VALUATION_TEXTS.stockSection}>
        <CustomTextField
          label={VALUATION_TEXTS.article}
          value={form?.article_name || ''}
          disabled
          sx={span(12)}
        />
        <CustomTextField
          label={VALUATION_TEXTS.warehouse}
          value={form?.warehouse_name || ''}
          disabled
          sx={span(6)}
        />
        <CustomTextField
          label={VALUATION_TEXTS.physical}
          value={materialCell('quantity_on_hand', form?.quantity_on_hand)}
          disabled
          sx={span(3)}
        />
        <CustomTextField
          label={VALUATION_TEXTS.reserved}
          value={materialCell('quantity_reserved', form?.quantity_reserved)}
          disabled
          sx={span(3)}
        />
        <CustomTextField
          label={VALUATION_TEXTS.valued}
          value={materialCell('valued_quantity', form?.valued_quantity)}
          disabled
          sx={span(6)}
        />
        <CustomTextField
          label={VALUATION_TEXTS.unvalued}
          value={materialCell('unvalued_quantity', form?.unvalued_quantity)}
          disabled
          sx={span(6)}
        />
      </DrawerFormSection>

      <DrawerFormSection title={VALUATION_TEXTS.openingSection}>
        <DecimalTextField
          label={VALUATION_TEXTS.amount}
          name="unit_cost_ars"
          decimals={2}
          value={form?.unit_cost_ars ?? ''}
          onChange={onChange}
          required
          sx={span(4)}
        />
        <CustomTextArea
          label={VALUATION_TEXTS.notes}
          placeholder={VALUATION_TEXTS.notesPlaceholder}
          name="source_notes"
          value={form?.source_notes || ''}
          onChange={onChange}
          required
          sx={span(12)}
        />
        <CustomTextArea
          label={VALUATION_TEXTS.evidence}
          placeholder={VALUATION_TEXTS.evidencePlaceholder}
          name="evidence"
          value={form?.evidence || ''}
          onChange={onChange}
          required
          sx={span(12)}
        />
      </DrawerFormSection>

      {preview && (
        <Box sx={{ mx: { xs: 1.25, md: 2 } }}>
          <DrawerFormPreview
            title={VALUATION_TEXTS.previewTitle}
            subtitle={`${VALUATION_TEXTS.valuedAsOf} ${preview.quote_as_of || '-'}`}
            badge={(
              <DrawerFormActionSummary
                label={VALUATION_TEXTS.previewTotal}
                value={materialMoney(preview.total_value_ars)}
              />
            )}
          >
            <DrawerFormActionSummary
              label={VALUATION_TEXTS.previewQuantity}
              value={materialCell('unvalued_quantity', preview.unvalued_quantity)}
            />
          </DrawerFormPreview>
        </Box>
      )}
    </Box>
  </DrawerForm>
);

export default MaterialOpeningDrawer;
